import React from 'react'
import classnames from 'classnames'
import { FilterLabeledControl } from '../../components/Controls'
import { SortHeaderButton } from '../../components/SortHeaderButton'
import { compareSortValues, type SortValue } from '../../sortUtils'

// Shared bits for the Detail tab's grouped (tenant-rollup) tables — §8 sort
// convention: when "Group by tenant" is on, sorting applies to the rollup rows
// (expanded sub-rows stay attached under their parent in fixed order); when off,
// sorting applies to the flat per-meter rows. New column → asc; same column →
// toggle. Active direction highlighted on the stacked sort icon (indigo).

export type { SortValue } from '../../sortUtils'

export interface GroupSort {
  key: string | null
  direction: 'asc' | 'desc'
}

/** §8: new column → asc; same column → toggle. */
export const nextSort = (prev: GroupSort, key: string): GroupSort =>
  prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }

/** Stable-sort a copy of `rows` by `value` under `sort` (no-op when unsorted). */
export const sortRows = <T,>(rows: T[], sort: GroupSort, value: ((row: T) => SortValue) | undefined): T[] => {
  if (sort.key == null || value == null) return rows
  return rows
    .map((row, index) => ({ row, index }))
    .sort((x, y) => {
      const cmp = compareSortValues(value(x.row), value(y.row), sort.direction)
      return cmp !== 0 ? cmp : x.index - y.index
    })
    .map((d) => d.row)
}

export const GroupChevron: React.FC<{ open: boolean }> = ({ open }) => (
  <span
    aria-hidden
    className={classnames('inline-block w-3 text-slate-400 transition-transform', open && 'rotate-90')}
  >
    ▸
  </span>
)

export interface SortableThProps {
  label: React.ReactNode
  align?: 'left' | 'right' | 'center'
  active: boolean
  direction: 'asc' | 'desc'
  onClick: () => void
  /** WEB-6569: hover tooltip describing the column (from the columnHelp registry). */
  title?: string
}

export const SortableTh: React.FC<SortableThProps> = ({ label, align = 'left', active, direction, onClick, title }) => (
  <th
    scope="col"
    title={title}
    aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    className={classnames(
      'px-3 py-2 text-xs font-medium text-slate-500 dark:text-slate-400',
      align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
    )}
  >
    <SortHeaderButton label={label} align={align} active={active} direction={direction} onClick={onClick} />
  </th>
)

export type GroupView = 'tenant' | 'flat'

export interface GroupViewToolbarProps {
  view: GroupView
  onViewChange: (view: GroupView) => void
  /** Show Expand all / Collapse all (grouped view with multi-row groups). */
  canExpand: boolean
  allOpen: boolean
  onToggleAll: () => void
  ariaLabel: string
  /** Extra toolbar content (e.g. the Category Filter); rendered after the group-by picker. */
  actions?: React.ReactNode
}

const EXPAND_ALL_BUTTON =
  'text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300'

/** Tinted background for expanded group sub-rows (invoice / meter detail lines). */
export const GROUP_SUB_ROW_CLASS =
  'bg-slate-50 transition-colors hover:bg-slate-100 dark:bg-slate-700/50 dark:hover:bg-slate-600/45'

export const GroupViewToolbar: React.FC<GroupViewToolbarProps> = ({
  view,
  onViewChange,
  canExpand,
  allOpen,
  onToggleAll,
  ariaLabel,
  actions
}) => (
  <div className="mb-2 flex flex-wrap items-center justify-between gap-4">
    <div className="flex flex-wrap items-center justify-start gap-4">
      <FilterLabeledControl label="Group by">
        <div role="group" aria-label={ariaLabel}>
          <button
            type="button"
            aria-label="Group by tenant"
            aria-pressed={view === 'tenant'}
            onClick={() => onViewChange(view === 'tenant' ? 'flat' : 'tenant')}
            className={classnames(
              'rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium transition-colors dark:border-slate-700',
              view === 'tenant'
                ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
            )}
          >
            Tenant
          </button>
        </div>
      </FilterLabeledControl>
      {actions}
    </div>
    {view === 'tenant' && canExpand && (
      <button type="button" onClick={onToggleAll} className={EXPAND_ALL_BUTTON}>
        {allOpen ? 'Collapse all' : 'Expand all'}
      </button>
    )}
  </div>
)
